import { z } from 'zod';
import { ulidSchema } from './id';
import { RANK_MAX_LENGTH } from './rank';

export const CUSTOM_FIELD_TYPES = [
  'text',
  'number',
  'checkbox',
  'date',
  'dropdown',
] as const;

export const CUSTOM_FIELD_NAME_MAX_LENGTH = 255;
export const CUSTOM_FIELD_OPTION_LABEL_MAX_LENGTH = 255;
export const CUSTOM_FIELD_TEXT_VALUE_MAX_LENGTH = 2048;

export const customFieldTypeSchema = z.enum(CUSTOM_FIELD_TYPES);
export type CustomFieldType = z.infer<typeof customFieldTypeSchema>;

const customFieldPositionSchema = z.string().min(1).max(RANK_MAX_LENGTH);
const timestampViewSchema = z.string().min(1);
const customFieldInputDateSchema = z.iso.datetime({ offset: true });

export const customFieldOptionSchema = z.object({
  id: ulidSchema,
  definitionId: ulidSchema,
  label: z.string().min(1).max(CUSTOM_FIELD_OPTION_LABEL_MAX_LENGTH),
  position: customFieldPositionSchema,
  createdAt: timestampViewSchema,
});

export type CustomFieldOption = z.infer<typeof customFieldOptionSchema>;

export const customFieldDefinitionSchema = z.object({
  id: ulidSchema,
  boardId: ulidSchema,
  name: z.string().min(1).max(CUSTOM_FIELD_NAME_MAX_LENGTH),
  type: customFieldTypeSchema,
  position: customFieldPositionSchema,
  hidden: z.boolean(),
  showOnFront: z.boolean(),
  options: z.array(customFieldOptionSchema),
  createdAt: timestampViewSchema,
  updatedAt: timestampViewSchema,
});

export type CustomFieldDefinition = z.infer<typeof customFieldDefinitionSchema>;

export const customFieldValueSchema = z.discriminatedUnion('type', [
  z.object({
    definitionId: ulidSchema,
    type: z.literal('text'),
    value: z.string().min(1).max(CUSTOM_FIELD_TEXT_VALUE_MAX_LENGTH),
  }),
  z.object({
    definitionId: ulidSchema,
    type: z.literal('number'),
    value: z.number().finite(),
  }),
  z.object({
    definitionId: ulidSchema,
    type: z.literal('checkbox'),
    value: z.literal(true),
  }),
  z.object({
    definitionId: ulidSchema,
    type: z.literal('date'),
    value: z.string().min(1),
  }),
  z.object({
    definitionId: ulidSchema,
    type: z.literal('dropdown'),
    optionId: ulidSchema,
    label: z.string().min(1).max(CUSTOM_FIELD_OPTION_LABEL_MAX_LENGTH),
  }),
]);

export type CustomFieldValue = z.infer<typeof customFieldValueSchema>;

export const cardCustomFieldSchema = customFieldDefinitionSchema.extend({
  value: customFieldValueSchema.nullable(),
});

export type CardCustomField = z.infer<typeof cardCustomFieldSchema>;

export const cardCustomFieldBadgeSchema = z.object({
  definitionId: ulidSchema,
  name: z.string().min(1).max(CUSTOM_FIELD_NAME_MAX_LENGTH),
  type: customFieldTypeSchema,
  value: customFieldValueSchema,
});

export type CardCustomFieldBadge = z.infer<typeof cardCustomFieldBadgeSchema>;

const customFieldCreateBaseSchema = {
  name: z.string().trim().min(1).max(CUSTOM_FIELD_NAME_MAX_LENGTH),
  hidden: z.boolean().default(false),
  showOnFront: z.boolean().default(false),
} as const;

export const createCustomFieldDefinitionBodySchema = z.discriminatedUnion('type', [
  z.strictObject({
    ...customFieldCreateBaseSchema,
    type: z.literal('text'),
  }),
  z.strictObject({
    ...customFieldCreateBaseSchema,
    type: z.literal('number'),
  }),
  z.strictObject({
    ...customFieldCreateBaseSchema,
    type: z.literal('checkbox'),
  }),
  z.strictObject({
    ...customFieldCreateBaseSchema,
    type: z.literal('date'),
  }),
  z.strictObject({
    ...customFieldCreateBaseSchema,
    type: z.literal('dropdown'),
    options: z
      .array(z.string().trim().min(1).max(CUSTOM_FIELD_OPTION_LABEL_MAX_LENGTH))
      .default([]),
  }),
]);

export type CreateCustomFieldDefinitionBody = z.infer<
  typeof createCustomFieldDefinitionBodySchema
>;

export const customFieldNamePatchBodySchema = z.strictObject({
  name: z.string().trim().min(1).max(CUSTOM_FIELD_NAME_MAX_LENGTH),
});

export const customFieldHiddenPatchBodySchema = z.strictObject({
  hidden: z.boolean(),
});

export const customFieldShowOnFrontPatchBodySchema = z.strictObject({
  showOnFront: z.boolean(),
});

export const customFieldPositionPatchBodySchema = z.strictObject({
  previousDefinitionId: ulidSchema.nullable().default(null),
  nextDefinitionId: ulidSchema.nullable().default(null),
});

export const createCustomFieldOptionBodySchema = z.strictObject({
  label: z.string().trim().min(1).max(CUSTOM_FIELD_OPTION_LABEL_MAX_LENGTH),
});

export const customFieldOptionLabelPatchBodySchema = z.strictObject({
  label: z.string().trim().min(1).max(CUSTOM_FIELD_OPTION_LABEL_MAX_LENGTH),
});

export const customFieldOptionPositionPatchBodySchema = z.strictObject({
  previousOptionId: ulidSchema.nullable().default(null),
  nextOptionId: ulidSchema.nullable().default(null),
});

export const customFieldValuePatchBodySchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('text'),
    value: z.string().max(CUSTOM_FIELD_TEXT_VALUE_MAX_LENGTH),
  }),
  z.strictObject({
    type: z.literal('number'),
    value: z.number().finite().nullable(),
  }),
  z.strictObject({
    type: z.literal('checkbox'),
    value: z.boolean(),
  }),
  z.strictObject({
    type: z.literal('date'),
    value: customFieldInputDateSchema.nullable(),
  }),
  z.strictObject({
    type: z.literal('dropdown'),
    optionId: ulidSchema.nullable(),
  }),
]);

export type CustomFieldValuePatchBody = z.infer<typeof customFieldValuePatchBodySchema>;

export type CustomFieldValueMutation =
  | { readonly type: 'text'; readonly action: 'set'; readonly value: string }
  | { readonly type: 'number'; readonly action: 'set'; readonly value: number }
  | { readonly type: 'checkbox'; readonly action: 'set'; readonly value: true }
  | { readonly type: 'date'; readonly action: 'set'; readonly value: string }
  | { readonly type: 'dropdown'; readonly action: 'set'; readonly optionId: string }
  | { readonly type: CustomFieldType; readonly action: 'clear' };

export function normalizeCustomFieldValuePatchBody(
  body: CustomFieldValuePatchBody,
): CustomFieldValueMutation {
  switch (body.type) {
    case 'text':
      return body.value.trim() === ''
        ? { type: 'text', action: 'clear' }
        : { type: 'text', action: 'set', value: body.value.trim() };
    case 'number':
      return body.value === null
        ? { type: 'number', action: 'clear' }
        : { type: 'number', action: 'set', value: body.value };
    case 'checkbox':
      return body.value
        ? { type: 'checkbox', action: 'set', value: true }
        : { type: 'checkbox', action: 'clear' };
    case 'date':
      return body.value === null
        ? { type: 'date', action: 'clear' }
        : { type: 'date', action: 'set', value: body.value };
    case 'dropdown':
      return body.optionId === null
        ? { type: 'dropdown', action: 'clear' }
        : { type: 'dropdown', action: 'set', optionId: body.optionId };
  }
}
